// eslint-disable no-negated-condition
// SAPI_TODO: better export
import {
  FacetFilter,
  FacetFilters,
  Hotel as SapiHotel,
  HotelSearchParameters as SapiHotelSearchParameters,
  LocationSearchParameters as SapiLocationSearchParameters,
  PlaceAnchor as SapiPlaceAnchor,
  PlaceSearchParameters as SapiPlaceSearchParameters,
  QuerySearchParameters as SapiQuerySearchParameters,
  SearchResults
} from '@findhotel/sapi/dist/types/packages/core/src/types'
import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {getVclidCookie} from 'modules/cookies/selectors'
import {getAnonymousId, getSeoActiveAttributes} from 'modules/meta/selectors'
import {
  HOSTEL_PROPERTY_TYPE_ID,
  SORT_FIELD_PRICE
} from 'modules/search/constants'
import {
  getIsStickyOtaMode,
  getOfferModeOverride,
  getUseBoVioFirstProfile
} from 'modules/search/selectors'
import {SortOrder} from 'modules/search/types'
// eslint-disable-next-line no-restricted-imports
import {toggle} from 'opticks'
import {mergeDeepRight} from 'ramda'
import {AppThunk, RootState} from 'store'
import {
  generateSearchParameters,
  GenerateSearchParametersContext
} from 'utils/server/searchParameters/generateSearchParameters'

import {getBrand} from '@vio/core/src/_web/brand/modules/selectors'
import {AvailabilityEntity} from '@vio/core/src/availability/types'
import {
  type OfferSearchReceivedPayload,
  offersSearchReceived
} from '@vio/core/src/sapi/services/searchApi/action'
import {Anchor, SearchParameters} from '@vio/core/src/sapi/types'
import {transformFacetsToArray} from '@vio/core/src/sapi/utils'

import {AnchorType, ModuleState} from './constants'
import {OldHotelOfferEntity} from './offersMapping'
import {getHotelIdsLastPage, getIsAppLockedDealAudience} from './selectors'
import {getSearchParameters} from './selectors'
import {
  applyHotelEntitiesPriceSorting,
  getHotelIdsWithOffers,
  removeOrchestrationParams
} from './utils'

import type {
  FacetsAsArray,
  SapiResultSearchParameters
} from '@vio/core/src/sapi/types'

export type Hotel = SapiHotel
export type {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
type Facets = Record<string, Record<string, number>>
export type HotelOfferEntity = OldHotelOfferEntity
export type PlaceAnchor = SapiPlaceAnchor
export type HotelSearchParameters = SapiHotelSearchParameters
export type PlaceSearchParameters = SapiPlaceSearchParameters
export type LocationSearchParameters = SapiLocationSearchParameters
export type QuerySearchParameters = SapiQuerySearchParameters

type HotelId = Hotel['objectID']

export type SearchStartedPayload = Pick<State, 'searchId' | 'searchParameters'>

type ParametersReceivedPayload = Pick<State, 'searchParameters' | 'isSeoSRP'>

export interface AnchorReceivedPayload
  extends Pick<State, 'anchor' | 'anchorHotelId' | 'hotelEntities'> {
  anchorType: string
  anchorHotel?: Hotel
  searchParameters: SapiResultSearchParameters
}

interface HotelsReceivedPayload
  extends Pick<
    State,
    'hotelEntities' | 'hotelsHaveStaticPosition' | 'resultsCountTotal'
  > {
  hotelIds: HotelId[]
  resultsCount: number
  offset: number
  facets: Facets
  facetFilters: FacetFilters
}

export interface OffersReceivedPayload {
  hotelOfferEntities: State['hotelOfferEntities']
}

export {AnchorType, ModuleState}

export interface State {
  anchorHotelId?: string
  anchorType?: AnchorType
  anchor?: Anchor
  facets?: FacetsAsArray
  searchFacets?: SearchResults['facets']
  moduleState: ModuleState
  /** Search parameters input */
  searchParameters?: SearchParameters
  /** Whether the current search is a SEO SRP (derived from the `taxonomy` route param) */
  isSeoSRP?: boolean
  /** Actual search parameters returned by SAPI */
  searchedParameters?: SearchParameters
  hasMoreResults?: boolean
  hotelsHaveStaticPosition?: boolean
  showUnavailable?: boolean
  // Added for fec27873-similar-hotels: keep supplementary pins separate from main results.
  similarHotelsMapResults?: {
    scope: string
    searchId: string
    hotelIds: HotelId[]
  }
  hotelEntities?: Record<HotelId, Hotel>
  hotelOfferEntities?: Record<HotelId, HotelOfferEntity>
  hotelIds?: HotelId[][]
  hotelIdsRaw?: HotelId[][]
  searchId?: string
  sapiPage: number
  page: number
  resultsCountTotal: number
  requestedResultsCount: number
  requestedOffset: number
  countNoReturnedResults: number
  hasFirstSearchResults: boolean
  resultsHaveHostels: boolean
  resultsExcludeHostels: boolean
  hotelAvailabilityPrices: AvailabilityEntity
  hotelAvailabilityPendingRequests: number
  anchorComplete: boolean
  hotelsComplete: boolean
  optimizeRooms?: boolean
  // Added as part of feature-srp-prefetch-pagination-results
  hasPrefetchedResults?: boolean
  newHotelIds?: HotelId[]
  neighborhoods?: FacetFilter[]
  brands?: FacetFilter[]
  // Added as part of feature-srp-refresh-price
  isRefetchingOffers?: boolean
}

export const initialState: State = {
  anchorHotelId: undefined,
  anchorType: undefined,
  anchor: undefined,
  facets: undefined,
  searchFacets: undefined,
  moduleState: ModuleState.Idle,
  searchParameters: undefined,
  isSeoSRP: false,
  searchedParameters: undefined,
  hasMoreResults: undefined,
  hotelsHaveStaticPosition: undefined,
  showUnavailable: undefined,
  similarHotelsMapResults: undefined,
  hotelEntities: undefined,
  hotelOfferEntities: undefined,
  hotelIds: undefined,
  hotelIdsRaw: undefined,
  searchId: undefined,
  sapiPage: 0,
  page: 0,
  resultsCountTotal: 0,
  requestedResultsCount: 0,
  requestedOffset: 0,
  countNoReturnedResults: 0,
  hasFirstSearchResults: false,
  resultsHaveHostels: false,
  resultsExcludeHostels: false,
  hotelAvailabilityPrices: {},
  hotelAvailabilityPendingRequests: 0,
  anchorComplete: false,
  hotelsComplete: false,
  // Added as part of feature-srp-prefetch-pagination-results
  hasPrefetchedResults: false,
  newHotelIds: undefined,
  neighborhoods: undefined,
  brands: undefined,
  // Added as part of feature-srp-refresh-price
  isRefetchingOffers: false
}

const keptOnNewSearch = new Set<keyof State>([
  'searchParameters',
  'hasFirstSearchResults',
  'resultsHaveHostels',
  'resultsExcludeHostels',
  // Added as part of feature-srp-refresh-price
  // A refresh starts a new search and stays in progress until it completes
  'isRefetchingOffers'
])

const hasHostel = (hotelEntities: State['hotelEntities']) =>
  Object.values(hotelEntities ?? {}).some(
    hotel => hotel?.propertyTypeId === Number(HOSTEL_PROPERTY_TYPE_ID)
  )

const {actions, reducer} = createSlice({
  name: 'sapiSearch',
  initialState,
  reducers: {
    setSimilarHotelsMapResults: (
      state,
      {payload}: PayloadAction<State['similarHotelsMapResults']>
    ) => {
      if (payload && payload.searchId !== state.searchId) return
      state.similarHotelsMapResults = payload
    },
    setSearchParameters: (
      state,
      {payload}: PayloadAction<ParametersReceivedPayload>
    ) => {
      state.searchParameters = payload.searchParameters
      state.isSeoSRP = payload.isSeoSRP
    },
    newSearch: state => {
      for (const key of Object.keys(initialState)) {
        if (!keptOnNewSearch.has(key as keyof State)) {
          state[key] = initialState[key]
        }
      }
    },
    searchStarted: (state, {payload}: PayloadAction<SearchStartedPayload>) => {
      const {searchParameters, searchId} = payload
      state.similarHotelsMapResults = undefined
      state.searchedParameters = removeOrchestrationParams(searchParameters)
      state.searchId = searchId
      state.moduleState = ModuleState.HotelsPending
    },
    anchorReceived: (
      state,
      {payload}: PayloadAction<AnchorReceivedPayload>
    ) => {
      const {
        anchor,
        hotelEntities,
        anchorHotelId,
        anchorType,
        searchParameters
      } = payload
      state.anchorType =
        anchorType === 'hotel' ? AnchorType.hotel : AnchorType.place
      state.anchor = state.anchor ?? anchor
      state.anchorHotelId = anchorHotelId
      state.hotelEntities = hotelEntities
      state.searchedParameters = removeOrchestrationParams(
        searchParameters as SearchParameters
      )
    },
    hotelsReceived: (
      state,
      {payload}: PayloadAction<HotelsReceivedPayload>
    ) => {
      const {
        facets,
        facetFilters,
        hotelEntities,
        hotelIds,
        hotelsHaveStaticPosition,
        resultsCountTotal,
        resultsCount,
        offset
      } = payload
      let hasMoreResults = false
      let newHotels = state.hotelEntities

      const newHotelIds = [...(state.hotelIds ?? [])]
      const newHotelIdsRaw = [...(state.hotelIdsRaw ?? [])]

      if (hotelIds?.length) {
        hasMoreResults = resultsCount + offset < resultsCountTotal

        newHotels = {
          ...state.hotelEntities,
          ...hotelEntities
        }

        newHotelIds.push(hotelIds)
        newHotelIdsRaw.push(hotelIds)
      }

      state.facets = transformFacetsToArray(facets)
      state.searchFacets = facets
      state.hotelEntities = newHotels
      state.hotelIds = newHotelIds
      state.hotelIdsRaw = newHotelIdsRaw
      state.requestedResultsCount = resultsCount
      state.requestedOffset = offset
      state.hasMoreResults = hasMoreResults
      state.brands = facetFilters?.brands
      state.neighborhoods = facetFilters?.neighborhoods
      state.hasFirstSearchResults = true
      state.resultsHaveHostels = hasHostel(newHotels)
      state.resultsExcludeHostels = Boolean(
        state.searchedParameters?.filters?.noHostels
      )

      // Skip for pagination
      if (!offset) {
        state.resultsCountTotal = resultsCountTotal
        state.hotelsHaveStaticPosition = hotelsHaveStaticPosition
      }
    },
    offersRequested: state => {
      state.moduleState = ModuleState.OffersPending
    },
    offersReceived: (
      state,
      {payload}: PayloadAction<OffersReceivedPayload>
    ) => {
      const {hotelOfferEntities} = payload

      const newHotelOfferEntities = {
        ...state.hotelOfferEntities,
        ...hotelOfferEntities
      }
      state.hotelOfferEntities = newHotelOfferEntities

      if (state.searchedParameters?.sortField === SORT_FIELD_PRICE) {
        const hotelIdsSortedByPrice = applyHotelEntitiesPriceSorting(
          hotelOfferEntities,
          state.searchedParameters?.sortOrder as SortOrder,
          state.searchedParameters?.priceDisplay,
          state.searchParameters?.optimizeRooms
        )

        state.hotelIds[state.sapiPage] = hotelIdsSortedByPrice
        state.hotelIdsRaw[state.sapiPage] = hotelIdsSortedByPrice
      }
    },
    anchorOffersReceived: (
      state,
      {payload}: PayloadAction<HotelOfferEntity>
    ) => {
      state.hotelOfferEntities = {
        ...state.hotelOfferEntities,
        [state.anchorHotelId]: payload
      }
    },
    anchorOffersComplete: state => {
      state.anchorComplete = true
    },
    hotelOffersComplete: state => {
      state.hotelsComplete = true
    },
    // We return to searchCompleted after the first and each subsequent page
    searchCompleted: state => {
      const hotelIds = getHotelIdsLastPage({sapiSearch: state} as RootState)
      const newHotelIds = [...(state.hotelIds ?? [])]

      const hotelIdsForHotelsWithOffers = getHotelIdsWithOffers(
        hotelIds,
        state.hotelOfferEntities
      )

      newHotelIds[state.sapiPage] = state.isSeoSRP
        ? hotelIds
        : hotelIdsForHotelsWithOffers

      if (hotelIdsForHotelsWithOffers.length) {
        state.countNoReturnedResults = 0
      } else {
        state.countNoReturnedResults += 1

        // Added as part of feature-srp-prefetch-pagination-results
        if (toggle('feature-srp-prefetch-pagination-results', false, true)) {
          state.hasPrefetchedResults = false
          state.page += 1
        }
      }
      state.moduleState = ModuleState.SearchCompleted
      // Added as part of feature-srp-refresh-price
      state.isRefetchingOffers = false
      state.hotelIds = newHotelIds
      state.anchorComplete = true
      // Added as part of feature-srp-prefetch-pagination-results
      state.newHotelIds = hotelIdsForHotelsWithOffers
    },
    moreHotelsRequested: state => {
      state.moduleState = ModuleState.MoreHotelsPending
    },
    setNextPage: state => {
      state.page += 1
    },
    // Pagination needs this for every user, so keep it when removing feature-srp-refresh-price
    setNextSapiPage: state => {
      state.sapiPage += 1
    },
    // Added as part of feature-srp-prefetch-pagination-results
    setHasPrefetchedResults: (state, {payload}: PayloadAction<boolean>) => {
      state.hasPrefetchedResults = payload
    },
    hotelsAvailabilityRequested: state => {
      state.hotelAvailabilityPendingRequests += 1
    },
    hotelsAvailabilityReceived: (state, {payload}) => {
      state.hotelAvailabilityPrices = mergeDeepRight(
        state.hotelAvailabilityPrices,
        payload
      )
    },
    hotelsAvailabilityCompleted: (state, {payload}) => {
      state.hotelAvailabilityPrices = mergeDeepRight(
        state.hotelAvailabilityPrices,
        payload
      )
      state.hotelAvailabilityPendingRequests -= 1
    },
    // Added as part of feature-srp-refresh-price
    // Refetching keeps the hotels, so newSearch doesn't run. Reset the flags it
    // would, or the first offers request to finish completes the whole search.
    offersRefetchStarted: state => {
      state.isRefetchingOffers = true
      state.hotelsComplete = false
      state.anchorComplete = false
      state.moduleState = ModuleState.OffersPending
    },
    // Added as part of feature-srp-refresh-price
    // The hotels and the offers that already arrived are still valid, so show them
    offersRefetchFailed: state => {
      state.isRefetchingOffers = false
      state.hotelsComplete = true
      state.anchorComplete = true
      state.moduleState = ModuleState.SearchCompleted
    }
  },
  extraReducers: builder => {
    // Syncing offers with state only for analytics purposes
    builder.addCase(
      offersSearchReceived.type,
      (state, action: PayloadAction<OfferSearchReceivedPayload>) => {
        const {offers, parameters} = action.payload || {}
        const forceSkip = !parameters.shouldUpdateOfferEntities
        if (!offers || forceSkip) return
        const hotelId = offers.id
        state.hotelOfferEntities = {
          ...state.hotelOfferEntities,
          [hotelId]: offers
        }
      }
    )
  }
})

export default reducer

export const {
  newSearch,
  setSimilarHotelsMapResults,
  searchStarted,
  setSearchParameters,
  anchorReceived,
  hotelsReceived,
  offersRequested,
  offersReceived,
  anchorOffersReceived,
  anchorOffersComplete,
  hotelOffersComplete,
  searchCompleted,
  moreHotelsRequested,
  setNextPage,
  setNextSapiPage,
  setHasPrefetchedResults,
  hotelsAvailabilityRequested,
  hotelsAvailabilityReceived,
  hotelsAvailabilityCompleted,
  offersRefetchStarted,
  offersRefetchFailed
} = actions

/**
 * Since anchor and hotel offer searches are split we have no single callback
 * Check whether both components are completed and trigger `searchCompleted`
 *
 * @returns void
 */
export const checkSearchCompleted = (): AppThunk => (dispatch, getState) => {
  const {
    sapiSearch: {searchParameters, anchorComplete, hotelsComplete}
  } = getState()

  const isAllComplete = searchParameters.hotelId
    ? anchorComplete && hotelsComplete
    : hotelsComplete

  if (isAllComplete) dispatch(searchCompleted())
}

/**
 * Compute new search API parameters from a URL change
 *
 * @remarks
 * !WARNING: this action runs on the server AND client.
 * Guidelines:
 * - Keep these functions pure, do not read from URL or cookies directly
 * - Keep business logic in `generateSearchParameters`, this thunk gathers the inputs
 * - The provided `toggle` may be used for experiments here
 * - Ensure any required Redux state is available in preloaded server state
 * - Ensure any required segmentation attributes are provided on the server
 */
export const syncSearchParameters =
  (
    payload: Pick<
      GenerateSearchParametersContext,
      | 'queryParams'
      | 'cookies'
      | 'user'
      | 'geolocation'
      | 'taxonomy'
      | 'isStickyOtaMode'
    >
  ): AppThunk =>
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (dispatch, getState, {toggle}) => {
    const {queryParams, cookies, user, geolocation, taxonomy} = payload

    const state = getState()
    const brand = getBrand(state)
    const vclid = getVclidCookie(state)
    const anonymousId = getAnonymousId(state)
    const boVioFirstProfileIsEnabled = getUseBoVioFirstProfile(state)
    const isAppLockedDealAudience = getIsAppLockedDealAudience(state)
    // Caller-provided override (sessionStorage on client, req.cookies on
    // server) takes precedence over Redux. Redux can be transiently wrong on
    // the client between hydration and reconciliation; sessionStorage is the
    // tab-scoped source of truth.
    const isStickyOtaMode = payload.isStickyOtaMode ?? getIsStickyOtaMode(state)
    const seoAttributes = getSeoActiveAttributes(state)

    const searchParameters = generateSearchParameters({
      queryParams,
      cookies,
      anonymousId,
      brand,
      geolocation,
      user,
      useBoVioFirstProfile: boVioFirstProfileIsEnabled,
      isAppLockedDealAudience,
      isStickyOtaMode,
      offerModeOverride: getOfferModeOverride(state),
      vclid,
      taxonomy,
      seoAttributes
    })

    // Prevent overwriting known user params from server with uninitialized Cognito state on client
    const currentSearchParameters = getSearchParameters(state)
    if (
      searchParameters.tier === undefined &&
      currentSearchParameters?.tier !== undefined
    ) {
      searchParameters.tier = currentSearchParameters.tier
      searchParameters.cugDeals = currentSearchParameters.cugDeals
      searchParameters.emailDomain = currentSearchParameters.emailDomain
      searchParameters.userId = currentSearchParameters.userId
    }

    /**
     * Keep ALL logic inside `generateSearchParameters` to avoid server/client mismatches
     * We only provide non-URL/cookie state here
     */
    dispatch(
      setSearchParameters({searchParameters, isSeoSRP: Boolean(taxonomy)})
    )
  }
